import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, FileText, ShoppingCart, CreditCard, AlertTriangle, Scale, RefreshCw, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';

const TermsOfServiceInner = () => {
  const lastUpdated = "December 2025";

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Header */}
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors mb-8"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                <FileText className="w-8 h-8 text-black" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-black text-white">
                  <PageEditable path="hero.title" as="span" fallback="Terms of Service" />
                </h1>
                <p className="text-gray-400 mt-2">
                  <PageEditable path="hero.lastUpdatedLabel" as="span" fallback="Last Updated:" /> <PageEditable path="hero.lastUpdated" as="span" fallback={lastUpdated} />
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg prose-invert max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-300 text-lg leading-relaxed">
              Welcome to Casa Samba, an Afro-Brasilian cultural school of drumming, dance, and culture history. These Terms of Service ("Terms") govern your use of our website <strong className="text-white">casasamba.com</strong> and all related services, including purchasing products, digital content, course enrollments, and memberships (collectively, the "Services").
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mt-4">
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-300">
              By creating an account, making a purchase, or otherwise using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our <Link to="/privacy-policy" className="text-amber-400 hover:text-amber-300">Privacy Policy</Link>.
            </p>
            <p className="text-gray-300 mt-4">
              You must be at least 18 years old to use our Services. If you are under 18, you may only use our Services with the involvement of a parent or guardian.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">2. Account Registration</h2>
            <p className="text-gray-300">To access certain features, you may need to create an account. You agree to:</p>
            <ul className="text-gray-300 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information as needed</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="text-gray-300 mt-4">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <ShoppingCart className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">3. Products and Purchases</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Product Descriptions</h3>
            <p className="text-gray-300">
              We strive to provide accurate descriptions of our products and services. However, we do not warrant that product descriptions, pricing, or other content is accurate, complete, or error-free.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Pricing</h3>
            <p className="text-gray-300">
              All prices are listed in US Dollars (USD) unless otherwise noted. We reserve the right to change prices at any time without notice. Prices in effect at the time of your purchase will apply.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Order Acceptance</h3>
            <p className="text-gray-300">
              Your receipt of an order confirmation does not constitute acceptance of your order. We reserve the right to refuse or cancel any order for any reason, including but not limited to product availability, errors in pricing or product information, or suspected fraud.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">4. Payment Terms</h2>
            </div>
            <p className="text-gray-300">
              We accept payment via credit/debit cards through Stripe and PayPal. By providing payment information, you represent that you are authorized to use the payment method.
            </p>
            <p className="text-gray-300 mt-4">
              All payments are processed securely. You agree to pay all charges incurred by you or any users of your account at the prices in effect when such charges are incurred, including applicable taxes and fees.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Recurring Subscriptions</h3>
            <p className="text-gray-300">
              For membership subscriptions, you authorize us to charge your payment method on a recurring basis (monthly, quarterly, or annually as selected). You may cancel your subscription at any time through your account settings or by contacting us.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">5. Refund Policy</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Digital Products</h3>
            <p className="text-gray-300">
              Due to the nature of digital products (e-books, downloadable content, online courses), <strong className="text-white">all sales of digital products are final</strong>. Refunds are not provided for digital purchases once access has been granted.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Physical Products</h3>
            <p className="text-gray-300">
              Physical products may be returned within 30 days of purchase in their original condition. Shipping costs for returns are the responsibility of the customer unless the item was damaged or defective.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Class Registrations & Memberships</h3>
            <p className="text-gray-300">
              Class registration fees are non-refundable but may be transferred to a future class with at least 48 hours notice. Membership fees are non-refundable but memberships may be cancelled at any time to prevent future charges.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Exceptions</h3>
            <p className="text-gray-300">
              Refund requests for exceptional circumstances will be reviewed on a case-by-case basis. Please contact us to discuss your situation.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">6. Digital Content & Intellectual Property</h2>
            <p className="text-gray-300">
              All content on our website, including but not limited to text, graphics, logos, images, audio, video, e-books, course materials, and software, is the property of Escola de Samba Casa Samba or its content suppliers and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            
            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">License for Digital Purchases</h3>
            <p className="text-gray-300">
              When you purchase digital content, you are granted a limited, non-exclusive, non-transferable license to access and use that content for personal, non-commercial purposes. You may not:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li>Copy, reproduce, or distribute the content</li>
              <li>Modify, create derivative works, or reverse engineer</li>
              <li>Share your account credentials or transfer access</li>
              <li>Use the content for commercial purposes without authorization</li>
              <li>Remove any copyright or proprietary notices</li>
            </ul>

            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mt-6">
              <div className="flex items-center gap-2 mb-2">
                <Ban className="w-5 h-5 text-red-400" />
                <p className="text-red-400 font-bold m-0">Content Protection Notice</p>
              </div>
              <p className="text-gray-300 m-0">
                Our digital content is protected against unauthorized copying and distribution. Violation of these terms may result in immediate termination of access and legal action.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">7. Class & Event Participation</h2>
            <p className="text-gray-300">
              By registering for classes, workshops, or events, you acknowledge and agree that:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li>Physical activities involve inherent risks of injury</li>
              <li>You are physically able to participate or will consult a physician beforehand</li>
              <li>You will follow all safety instructions provided by instructors</li>
              <li>Escola de Samba Casa Samba is not liable for injuries resulting from participation</li>
              <li>You consent to being photographed or recorded for promotional purposes (you may opt out by informing us)</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">8. User Conduct</h2>
            <p className="text-gray-300">You agree not to:</p>
            <ul className="text-gray-300 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Use our Services for fraudulent purposes</li>
              <li>Interfere with the proper functioning of our website</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Harass, abuse, or harm other users or our staff</li>
              <li>Post false, misleading, or defamatory content</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">9. Disclaimers & Limitations of Liability</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">As-Is Basis</h3>
            <p className="text-gray-300">
              Our Services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Limitation of Liability</h3>
            <p className="text-gray-300">
              To the maximum extent permitted by law, Escola de Samba Casa Samba shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or related to your use of our Services.
            </p>
            <p className="text-gray-300 mt-4">
              Our total liability for any claims arising from these Terms or your use of our Services shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">10. Indemnification</h2>
            <p className="text-gray-300">
              You agree to indemnify, defend, and hold harmless Escola de Samba Casa Samba, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or related to your violation of these Terms or your use of our Services.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">11. Governing Law & Disputes</h2>
            </div>
            <p className="text-gray-300">
              These Terms shall be governed by and construed in accordance with the laws of the State of Louisiana, United States, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-300 mt-4">
              Any disputes arising from these Terms or your use of our Services shall be resolved through binding arbitration in New Orleans, Louisiana, in accordance with the rules of the American Arbitration Association. You waive any right to participate in a class action lawsuit.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">12. Changes to Terms</h2>
            <p className="text-gray-300">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our Services after any changes constitutes acceptance of the modified Terms.
            </p>
            <p className="text-gray-300 mt-4">
              We encourage you to review these Terms periodically for any updates.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">13. Severability</h2>
            <p className="text-gray-300">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">14. Contact Information</h2>
            <p className="text-gray-300">
              For questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-6 mt-4">
              <p className="text-white font-bold text-lg">Escola de Samba Casa Samba</p>
              <p className="text-gray-400">New Orleans, Louisiana</p>
              <p className="text-amber-400 mt-2">Email: info@casasamba.com</p>
            </div>
          </section>

          {/* Agreement Box */}
          <section className="mb-12">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
              <p className="text-amber-400 font-bold text-lg mb-2">By using our Services, you acknowledge that:</p>
              <ul className="text-gray-300 space-y-2">
                <li>You have read and understood these Terms of Service</li>
                <li>You agree to be bound by these Terms</li>
                <li>You have read and agree to our Privacy Policy</li>
              </ul>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Links */}
      <div className="border-t border-stone-800 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap justify-center gap-6">
          <Link to="/privacy-policy" className="text-amber-400 hover:text-amber-300 transition-colors">
            Privacy Policy
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            Home
          </Link>
          <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function TermsOfService() {
  return (
    <PageEditProvider slug="terms-of-service">
      <TermsOfServiceInner />
      <PageEditToolbar />
    </PageEditProvider>
  );
}
